import { useEffect, useMemo, useState } from 'react';
import { Marker, useMap } from 'react-map-gl/maplibre';
import Supercluster from 'supercluster';

import { MarkerItem } from '../MarkerItem';

import {
  realtyStatusMock,
  stageSelectDataMapValues,
} from '../../../../data/cardRealty';
import { colors } from '../../../../data/constants';

const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;

const handleColors = (status, stages) => {
  const checkStages = [];
  let markerColor = colors.redColor;
  let leftColor = colors.whiteColor;
  let rightColor = colors.whiteColor;
  let letter = 'A';

  if (stages && stages.length > 0) {
    stages.forEach(item => {
      switch (item.value) {
      case stageSelectDataMapValues.contact:
        markerColor = colors.greenColor;
        break;
      case stageSelectDataMapValues.introducer:
        markerColor = colors.yellowColor;
        break;
      case stageSelectDataMapValues.urgent:
        leftColor = colors.lightYellowColor;
        rightColor = colors.lightYellowColor;
        checkStages.push(item.value);
        break;
      case stageSelectDataMapValues.visit:
        leftColor = colors.lightPinkColor;
        rightColor = colors.lightPinkColor;
        checkStages.push(item.value);
        break;
      default:
        break;
      }
    });

    if (
      checkStages.includes(stageSelectDataMapValues.urgent) &&
      checkStages.includes(stageSelectDataMapValues.visit)
    ) {
      leftColor = colors.lightYellowColor;
      rightColor = colors.lightPinkColor;
    }
  }

  if (status || status === 0) {
    realtyStatusMock.forEach(item => {
      if (Number(item.value) === Number(status)) {
        letter = item.mapValue;
      }
    });
  }

  return { markerColor, leftColor, rightColor, letter };
};

// Cluster bubble size scales with count for spatial hierarchy.
const clusterSize = count => {
  if (count >= 100) return 52;
  if (count >= 25) return 44;
  return 36;
};

export const MarkerList = ({
  markers = [],
  handleActiveMarker = () => {},
  setActiveMarker = () => {},
  activeMarker,
  selectedId = null,
  renderPopupInline = true,
}) => {
  const { current: mapRef } = useMap();
  const [viewport, setViewport] = useState({ bounds: null, zoom: 10 });

  useEffect(() => {
    const map = mapRef?.getMap?.() ?? mapRef;
    if (!map) return;
    const update = () => {
      const b = map.getBounds();
      setViewport({
        bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        zoom: Math.round(map.getZoom()),
      });
    };
    update();
    map.on('move', update);
    return () => map.off('move', update);
  }, [mapRef]);

  const superclusterIndex = useMemo(() => {
    const index = new Supercluster({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
    });
    const points = markers
      .filter(m => m?.latitude != null && m?.longitude != null)
      .map(m => ({
        type: 'Feature',
        properties: { cluster: false, realty: m },
        geometry: {
          type: 'Point',
          coordinates: [Number(m.longitude), Number(m.latitude)],
        },
      }));
    index.load(points);
    return index;
  }, [markers]);

  const clusters = useMemo(() => {
    if (!viewport.bounds) return [];
    return superclusterIndex.getClusters(viewport.bounds, viewport.zoom);
  }, [viewport, superclusterIndex]);

  const handleClusterClick = (clusterId, longitude, latitude) => {
    const map = mapRef?.getMap?.() ?? mapRef;
    if (!map) return;
    const expansionZoom = Math.min(
      superclusterIndex.getClusterExpansionZoom(clusterId),
      20
    );
    map.flyTo({ center: [longitude, latitude], zoom: expansionZoom, duration: 400 });
  };

  if (!markers.length) return null;

  return (
    <>
      {clusters.map(feature => {
        const [longitude, latitude] = feature.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount, cluster_id } =
          feature.properties;

        if (isCluster) {
          const size = clusterSize(pointCount);
          return (
            <Marker
              key={`cluster-${cluster_id}`}
              longitude={longitude}
              latitude={latitude}
              anchor="center"
              onClick={e => {
                e.originalEvent.stopPropagation();
                handleClusterClick(cluster_id, longitude, latitude);
              }}
            >
              <div
                style={{ width: size, height: size }}
                className="flex items-center justify-center rounded-full text-white text-sm font-semibold shadow-lg bg-redColor border-2 border-white cursor-pointer"
              >
                {pointCount}
              </div>
            </Marker>
          );
        }

        const realty = feature.properties.realty;
        const markerColors = handleColors(
          realty?.realty_status,
          realty?.realty_stages
        );

        return (
          <MarkerItem
            key={realty.id}
            realty={realty}
            colors={markerColors}
            activeMarker={activeMarker}
            setActiveMarker={setActiveMarker}
            handleActiveMarker={handleActiveMarker}
            isSelected={
              selectedId != null && String(realty?.id) === String(selectedId)
            }
            renderPopupInline={renderPopupInline}
          />
        );
      })}
    </>
  );
};
